// Typed fetch wrappers over the frozen API surface, plus the SSE chat reader.
// The server is the source of truth; this module never reshapes payloads.

import type {
  AnalysisResponse,
  BoardSize,
  ChatMessage,
  GameDetail,
  GameMode,
  GameSummary,
  GradeResponse,
  HealthResponse,
  PlayMoveResponse,
  ScoreBreakdown,
  ScoreEstimateResponse,
  UndoResponse,
  Vertex,
} from '../shared/types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function isWarming(err: unknown): boolean {
  return err instanceof ApiError && (err.code === 'katago_warming' || err.status === 503);
}

/** True when a fetch rejected because its AbortSignal fired (navigation teardown). */
export function isAborted(err: unknown): boolean {
  return (
    (err instanceof DOMException || err instanceof Error) &&
    (err as { name?: string }).name === 'AbortError'
  );
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    if (isAborted(err)) throw err; // deliberate teardown, not a connectivity problem
    throw new ApiError(0, 'Cannot reach the server');
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    let code: string | null = null;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (typeof body.error === 'string') {
        code = body.error;
        message = body.message ?? body.error;
      } else if (typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, code);
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    signal,
  });
}

export interface CreateGameBody {
  boardSize: BoardSize;
  mode: GameMode;
  humanColor?: 'B' | 'W' | 'random';
  botLevel?: number;
}

export const api = {
  auth: (passphrase: string) => post<{ ok: true }>('/api/auth', { passphrase }),
  createGame: (body: CreateGameBody) => post<GameDetail>('/api/games', body),
  listGames: () => request<{ games: GameSummary[] }>('/api/games'),
  getGame: (id: number) => request<GameDetail>(`/api/games/${id}`),
  playMove: (id: number, vertex: Vertex) =>
    post<PlayMoveResponse>(`/api/games/${id}/moves`, { vertex }),
  botMove: (id: number, expectedMoveNumber: number) =>
    post<PlayMoveResponse>(`/api/games/${id}/bot-move`, { expectedMoveNumber }),
  undo: (id: number) => post<UndoResponse>(`/api/games/${id}/undo`),
  resume: (id: number) => post<GameDetail>(`/api/games/${id}/resume`),
  analysis: (id: number, body: { turn?: number; topN?: number }, signal?: AbortSignal) =>
    post<AnalysisResponse>(`/api/games/${id}/analysis`, body, signal),
  grade: (id: number, moveNumber: number, signal?: AbortSignal) =>
    request<GradeResponse>(`/api/games/${id}/grade/${moveNumber}`, { signal }),
  score: (id: number, signal?: AbortSignal) =>
    post<ScoreEstimateResponse>(`/api/games/${id}/score`, undefined, signal),
  finalize: (id: number, body: { deadStones: Vertex[]; forcedDame: Vertex[] }) =>
    post<ScoreBreakdown>(`/api/games/${id}/finalize`, body),
  chatHistory: (id: number, signal?: AbortSignal) =>
    request<{ messages: ChatMessage[] }>(`/api/games/${id}/chat`, { signal }),
  health: () => request<HealthResponse>('/api/health'),
};

// ---------------------------------------------------------------------------
// SSE chat stream: fetch + ReadableStream parsing of event/data blocks.
// Events: 'delta' {text} | 'tool' {name} | 'done' {} | 'error' {message}
// ---------------------------------------------------------------------------

export interface ChatStreamHandlers {
  onDelta(text: string): void;
  onTool(name: string): void;
  onError(message: string): void;
}

export async function streamChat(
  gameId: number,
  body: { message: string; cursor?: number },
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/games/${gameId}/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    let code: string | null = null;
    try {
      const err = (await res.json()) as { error?: string; message?: string };
      code = typeof err.error === 'string' ? err.error : null;
      message = err.message ?? code ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message, code);
  }
  if (!res.body) throw new ApiError(0, 'Chat stream had no body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done = false;

  const handleBlock = (block: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    const data = dataLines.join('\n');
    let parsed: Record<string, unknown> = {};
    if (data) {
      try {
        parsed = JSON.parse(data) as Record<string, unknown>;
      } catch {
        parsed = {};
      }
    }
    switch (event) {
      case 'delta':
        if (typeof parsed.text === 'string') handlers.onDelta(parsed.text);
        break;
      case 'tool':
        if (typeof parsed.name === 'string') handlers.onTool(parsed.name);
        break;
      case 'error':
        handlers.onError(typeof parsed.message === 'string' ? parsed.message : 'Chat failed');
        break;
      case 'done':
        done = true;
        break;
      default:
        break;
    }
  };

  for (;;) {
    const { done: eof, value } = await reader.read();
    if (eof) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (block.trim()) handleBlock(block);
      if (done) {
        void reader.cancel().catch(() => {}); // release the connection promptly
        return;
      }
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) handleBlock(buffer);
}
